import { useMemo } from "react";
import { useGetList, useGetOne } from "ra-core";
import { Clock, ExternalLink, Video } from "lucide-react";
import { Link } from "react-router";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { MemberAvatar } from "../shared/MemberAvatar";
import type { Appointment, Sale } from "../types";

// Sprint 8.1 — Right rail /appointments conforme cal-direction1.jsx Rail.
// 2 widgets : "Prochain RDV" (suivant l'heure courante) + "Charge équipe
// cette semaine" (count appointments par sales_id).

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function AppointmentRail() {
  // Prochain RDV : useGetList start_at > now ASC limit 1
  const nowIso = useMemo(() => new Date().toISOString(), []);
  const endOfWeekIso = useMemo(
    () => new Date(Date.now() + WEEK_MS).toISOString(),
    [],
  );

  const { data: nextList } = useGetList<Appointment>("appointments", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "start_at", order: "ASC" },
    filter: { "start_at@gte": nowIso },
  });
  const nextRdv = nextList?.[0];

  const { data: weekAppts } = useGetList<Appointment>("appointments", {
    pagination: { page: 1, perPage: 500 },
    filter: {
      "start_at@gte": new Date(Date.now() - WEEK_MS / 2).toISOString(),
      "start_at@lte": endOfWeekIso,
    },
  });

  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false },
  });

  const countBySale = useMemo(() => {
    const map = new Map<number, number>();
    (weekAppts ?? []).forEach((a) => {
      if (a.sales_id == null) return;
      const k = Number(a.sales_id);
      map.set(k, (map.get(k) ?? 0) + 1);
    });
    return map;
  }, [weekAppts]);

  const maxCount = Math.max(1, ...Array.from(countBySale.values()));

  return (
    <aside className="hidden xl:flex flex-col gap-4 w-[280px] shrink-0">
      {/* Prochain RDV widget */}
      <div className="rounded-xl border border-border bg-gradient-to-b from-violet-50 to-violet-100/30 dark:from-violet-950/30 dark:to-violet-900/10 dark:border-violet-900/40 p-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-violet-800 dark:text-violet-300 mb-2.5">
          {nextRdv ? "Prochain RDV" : "Aucun RDV à venir"}
        </h3>
        {nextRdv ? (
          <NextRdvCard appt={nextRdv} />
        ) : (
          <p className="text-[12.5px] text-muted-foreground">
            Pas de rendez-vous planifié pour le moment.
          </p>
        )}
      </div>

      {/* Charge équipe widget */}
      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Charge équipe · cette semaine
        </h3>
        <div className="flex flex-col gap-2.5">
          {(sales ?? []).map((s) => {
            const id = Number(s.id);
            const count = countBySale.get(id) ?? 0;
            const pct = (count / maxCount) * 100;
            return (
              <div key={s.id} className="flex items-center gap-2.5">
                <MemberAvatar salesId={id} size={22} />
                <div className="text-[12px] font-medium text-foreground min-w-0 truncate w-16">
                  {s.first_name ?? ""}
                </div>
                <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      id === 1 && "bg-orange-500",
                      id === 2 && "bg-emerald-500",
                      id === 3 && "bg-red-500",
                      id > 3 && "bg-violet-500",
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-[11px] text-muted-foreground tabular-nums w-6 text-right">
                  {count}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

function NextRdvCard({ appt }: { appt: Appointment }) {
  const startsIn = useMemo(() => {
    try {
      return formatDistanceToNowStrict(new Date(appt.start_at), {
        addSuffix: true,
        locale: fr,
      });
    } catch {
      return "";
    }
  }, [appt.start_at]);

  const startTime = new Date(appt.start_at).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const endTime = new Date(appt.end_at).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const { data: contact } = useGetOne(
    "contacts",
    { id: appt.contact_id ?? 0 },
    { enabled: appt.contact_id != null },
  );

  return (
    <>
      <div className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground mb-1">
        <Clock className="size-3" />
        {startTime} → {endTime}
        <span aria-hidden className="text-muted-foreground/50">
          ·
        </span>
        {startsIn}
      </div>
      <div className="text-[14px] font-semibold text-foreground mb-0.5 truncate">
        {appt.title}
      </div>
      <div className="text-[12px] text-muted-foreground flex items-center gap-1.5">
        <MemberAvatar salesId={appt.sales_id} size={18} />
        {contact && (
          <>
            <span>avec</span>
            <span className="font-medium text-foreground">
              {(contact as { first_name?: string; last_name?: string })
                .first_name ?? ""}{" "}
              {(contact as { first_name?: string; last_name?: string })
                .last_name ?? ""}
            </span>
          </>
        )}
      </div>
      <div className="flex gap-1.5 mt-3">
        {appt.location && appt.location.startsWith("http") && (
          <a
            href={appt.location}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-card border border-border text-[12px] hover:bg-muted/40"
          >
            <Video className="size-3" />
            Rejoindre
          </a>
        )}
        {appt.contact_id != null && (
          <Link
            to={`/contacts/${appt.contact_id}/show`}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-card border border-border text-[12px] hover:bg-muted/40"
          >
            <ExternalLink className="size-3" />
            Fiche
          </Link>
        )}
      </div>
    </>
  );
}
